import { useEffect, useRef, useState } from "react";
import { api, type BuscaResultado } from "../lib/api";

interface Props {
  onAbrirPcf: (numpcf: number) => void; // TELA-NUMPCF Enter -> consulta_producao CONSULTA
  onAbrirPedido: (numped: number) => void; // TELA-NUMPED Enter -> consulta_producao PEDIDO
}

/** Busca livre na tela principal: PCF, pedido, cliente ou produto. */
export function BuscaPcf({ onAbrirPcf, onAbrirPedido }: Props) {
  const [termo, setTermo] = useState("");
  const [resultados, setResultados] = useState<BuscaResultado[]>([]);
  const [aberto, setAberto] = useState(false);
  const [carregando, setCarregando] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);
  const debounce = useRef<number | null>(null);

  useEffect(() => {
    if (debounce.current) window.clearTimeout(debounce.current);
    const t = termo.trim();
    if (t.length < 2) {
      setResultados([]);
      setAberto(false);
      return;
    }
    debounce.current = window.setTimeout(() => {
      setCarregando(true);
      api
        .busca(t)
        .then((r) => {
          setResultados(r);
          setAberto(true);
        })
        .catch(() => setResultados([]))
        .finally(() => setCarregando(false));
    }, 300);
    return () => {
      if (debounce.current) window.clearTimeout(debounce.current);
    };
  }, [termo]);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setAberto(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const abrir = (numpcf: number) => {
    onAbrirPcf(numpcf);
    setAberto(false);
    setTermo("");
    setResultados([]);
  };

  const slug = (s: string) => s.toLowerCase().replace(/[^a-z]+/g, "-");

  return (
    <div className="busca-pcf" ref={boxRef}>
      <span className="busca-icone" aria-hidden="true">🔍</span>
      <input
        className="busca-input"
        type="search"
        placeholder="Buscar PCF, pedido, cliente ou produto…"
        value={termo}
        onChange={(e) => setTermo(e.target.value)}
        onFocus={() => {
          if (resultados.length) setAberto(true);
        }}
      />
      {aberto && (
        <div className="busca-dropdown">
          {carregando && <div className="busca-vazio">Buscando…</div>}
          {!carregando && resultados.length === 0 && (
            <div className="busca-vazio">Nada encontrado.</div>
          )}
          {resultados.map((r) => (
            <button key={r.numpcf} className="busca-item" onClick={() => abrir(r.numpcf)}>
              <div className="busca-item-l1">
                <strong>PCF {r.numpcf}</strong>
                {r.numped ? (
                  <span
                    className="busca-ped busca-ped-link"
                    title="Ver todas as PCFs deste pedido"
                    onClick={(e) => {
                      e.stopPropagation();
                      onAbrirPedido(r.numped ?? 0);
                      setAberto(false);
                      setTermo("");
                    }}
                  >
                    · Pedido {r.numped}
                  </span>
                ) : null}
                <span className={`busca-sit sit-${slug(r.situacPcf)}`}>{r.situacPcf}</span>
              </div>
              <div className="busca-item-l2">
                {r.cliente}
                {r.produto ? ` — ${r.produto}` : ""}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
